"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Container = void 0;
const tslib_1 = require("tslib");
const symbols_1 = require("./symbols");
const lodash_1 = tslib_1.__importDefault(require("lodash"));
const yieldToMain = () => {
    if (process.env.browser &&
        process.env.PACKAGE_NAME !== 'thunderbolt-ds' &&
        process.env.RENDERER_BUILD !== 'react-native') {
        return new Promise((resolve) => setTimeout(resolve, 0));
    }
};
function Container(parentContainerState) {
    const state = {
        instances: {},
        factories: {},
        instanceCache: {},
    };
    let taskDeadline = 0;
    const MAX_TASK_DURATION = 40;
    let container; // eslint-disable-line prefer-const
    function getDependencyData(depObject) {
        // @ts-expect-error
        const name = (depObject?.identifier || depObject);
        // @ts-expect-error
        const targetName = depObject?.name;
        // @ts-expect-error
        const isMulti = !!depObject?.multi;
        // @ts-expect-error
        const isOptional = !!depObject?.optional;
        return {
            isOptional,
            isMulti,
            name,
            targetName,
        };
    }
    function getInstances(name, targetName, containerState) {
        const instanceData = containerState.instances[name];
        if (!instanceData && !targetName) {
            return init(name, targetName, containerState);
        }
        if (targetName) {
            const moduleKey = targetName ? `${name.toString()}_${targetName.toString()}` : name;
            const namedValue = containerState.instances[moduleKey];
            if (!namedValue) {
                return init(name, targetName, containerState);
            }
            return namedValue;
        }
        return instanceData;
    }
    function getInstancesAsync(name, targetName, containerState) {
        const instanceData = containerState.instances[name];
        if (!instanceData && !targetName) {
            return initAsync(name, targetName, containerState);
        }
        if (targetName) {
            const moduleKey = targetName ? `${name.toString()}_${targetName.toString()}` : name;
            const namedValue = containerState.instances[moduleKey];
            if (!namedValue) {
                return initAsync(name, targetName, containerState);
            }
            return namedValue;
        }
        return instanceData;
    }
    function resolveInstances(name, targetName) {
        const instances = getInstances(name, targetName, state);
        if (instances) {
            return instances;
        }
        if (parentContainerState) {
            const parentInstance = getInstances(name, targetName, parentContainerState);
            if (parentInstance) {
                return parentInstance;
            }
        }
        return [];
    }
    async function resolveInstancesAsync(name, targetName) {
        const instances = await getInstancesAsync(name, targetName, state);
        if (instances) {
            return instances;
        }
        if (parentContainerState) {
            const parentInstance = await getInstancesAsync(name, targetName, parentContainerState);
            if (parentInstance) {
                return parentInstance;
            }
        }
        return [];
    }
    function resolveDependency(depObject, moduleName) {
        const { name, isMulti, isOptional, targetName } = getDependencyData(depObject);
        const instances = resolveInstances(name, targetName);
        if (!isMulti && !isOptional && instances.length === 0) {
            throw new Error(`Unbound dependency ${name.toString()} in module ${moduleName.toString()}`);
        }
        if (!isMulti && instances.length > 1) {
            throw new Error(`resolveDependency: Cannot get multiple instances of module ${name.toString()} without requesting multi in module ${moduleName.toString()}`);
        }
        return isMulti ? instances : instances[0];
    }
    async function yieldToMainIfNeeded() {
        if (performance.now() >= taskDeadline) {
            await yieldToMain();
            taskDeadline = performance.now() + MAX_TASK_DURATION;
        }
    }
    async function resolveDependencyAsync(depObject, moduleName) {
        if (process.env.browser) {
            await yieldToMainIfNeeded();
        }
        const { name, isMulti, isOptional, targetName } = getDependencyData(depObject);
        const instances = await resolveInstancesAsync(name, targetName);
        if (!isMulti && !isOptional && instances.length === 0) {
            throw new Error(`Unbound dependency ${name.toString()} in module ${moduleName.toString()}`);
        }
        if (!isMulti && instances.length > 1) {
            throw new Error(`resolveDependencyAsync: Cannot get multiple instances of module ${name.toString()} without requesting multi in module ${moduleName.toString()}`);
        }
        return { value: isMulti ? instances : instances[0] };
    }
    function executeFactory({ factory, provider, deps, name, }) {
        return provider
            ? factory(container)
            : factory(...deps.map((d) => resolveDependency(d, name)));
    }
    async function executeFactoryAsync({ factory, provider, deps, name, }) {
        const depsArray = [];
        for (const d of deps) {
            depsArray.push(await resolveDependencyAsync(d, name));
        }
        const resolvedDeps = depsArray.map((item) => item.value);
        const instance = provider
            ? factory(container)
            : factory(...resolvedDeps);
        return { value: instance };
    }
    function init(name, targetName, containerState) {
        const moduleKey = targetName ? `${name.toString()}_${targetName.toString()}` : name;
        const modules = containerState.factories[moduleKey];
        if (!modules) {
            return undefined;
        }
        return modules.map((mod) => {
            const { factoryId, factory, deps, provider } = mod;
            const instance = containerState.instanceCache[factoryId] || executeFactory({ provider, deps, name, factory });
            containerState.instanceCache[factoryId] = instance;
            containerState.instances[moduleKey] = containerState.instances[moduleKey] || [];
            containerState.instances[moduleKey].push(instance);
            return instance;
        });
    }
    async function initAsync(name, targetName, containerState) {
        const moduleKey = targetName ? `${name.toString()}_${targetName.toString()}` : name;
        const modules = containerState.factories[moduleKey];
        if (!modules) {
            return undefined;
        }
        const instances = [];
        for (const mod of modules) {
            const { deps, factory, provider, factoryId } = mod;
            const { value: instance } = containerState.instanceCache[factoryId]
                ? { value: containerState.instanceCache[factoryId] }
                : await executeFactoryAsync({
                    provider,
                    factory,
                    deps,
                    name,
                });
            containerState.instanceCache[factoryId] = instance;
            containerState.instances[moduleKey] = containerState.instances[moduleKey] || [];
            containerState.instances[moduleKey].push(instance);
            instances.push(instance);
        }
        return instances;
    }
    function registerProvider(name, factory, factoryId) {
        state.factories[name] = state.factories[name] || [];
        state.factories[name].push({ factory, deps: [], provider: true, factoryId });
    }
    function register(name, factory, factoryId) {
        const deps = factory[symbols_1.ModuleMetadataSymbol].dependencies;
        state.factories[name] = state.factories[name] || [];
        state.factories[name].push({ factory, deps, factoryId });
    }
    function registerWithTargetName(name, factory, targetName, factoryId) {
        const deps = factory[symbols_1.ModuleMetadataSymbol].dependencies;
        const key = `${name.toString()}_${targetName.toString()}`;
        state.factories[key] = state.factories[key] || [];
        state.factories[key].push({ factory, deps, factoryId });
    }
    function registerConstantValueWithTargetName(name, value, targetName, factoryId) {
        const key = `${name.toString()}_${targetName.toString()}`;
        state.factories[key] = state.factories[key] || [];
        state.factories[key].push({ factory: () => value, deps: [], factoryId });
    }
    function registerConstantValue(name, value, factoryId) {
        state.factories[name] = state.factories[name] || [];
        state.factories[name].push({ factory: () => value, deps: [], factoryId });
    }
    function unregister(name) {
        delete state.factories[name];
        delete state.instances[name];
    }
    function bind(...moduleNames) {
        return {
            to(factory) {
                const factoryId = lodash_1.default.uniqueId();
                moduleNames.forEach((name) => register(name, factory, factoryId));
                return {
                    whenTargetNamed(targetName) {
                        registerWithTargetName(moduleNames[0], factory, targetName, `${factoryId}_${targetName.toString()}`);
                    },
                };
            },
            toProvider(factory) {
                const factoryId = lodash_1.default.uniqueId();
                registerProvider(moduleNames[0], factory, factoryId);
                return {
                    whenTargetNamed(targetName) {
                        throw new Error(`calling whenTargetNamed ${targetName.toString()} with toProvider on module ${moduleNames[0].toString()} is not supported`);
                    },
                };
            },
            toConstantValue(value) {
                const factoryId = lodash_1.default.uniqueId();
                registerConstantValue(moduleNames[0], value, factoryId);
                return {
                    whenTargetNamed(targetName) {
                        registerConstantValueWithTargetName(moduleNames[0], value, targetName, `${factoryId}_${targetName.toString()}`);
                    },
                };
            },
        };
    }
    function rebind(...moduleNames) {
        moduleNames.forEach((name) => unregister(name));
        return bind(...moduleNames);
    }
    function createChild() {
        return Container(state);
    }
    const api = {
        bind,
        rebind,
        getNamed(name, targetName) {
            return resolveDependency({ identifier: name, name: targetName }, name);
        },
        getAllNamed(name, targetName) {
            return resolveDependency({ identifier: name, name: targetName, multi: true }, name);
        },
        async getNamedAsync(name, targetName) {
            return (await resolveDependencyAsync({ identifier: name, name: targetName }, name)).value;
        },
        async getAllAsync(name) {
            taskDeadline = process.env.browser ? performance.now() + MAX_TASK_DURATION : 0;
            return (await resolveDependencyAsync({ identifier: name, multi: true }, name)).value;
        },
        getAll(name) {
            return resolveDependency({ identifier: name, multi: true }, name);
        },
        get(name) {
            return resolveDependency({ identifier: name, optional: true }, name);
        },
        async getAsync(name) {
            taskDeadline = process.env.browser ? performance.now() + MAX_TASK_DURATION : 0;
            return (await resolveDependencyAsync({ identifier: name, optional: true }, name)).value;
        },
        load(...moduleLoaders) {
            moduleLoaders.forEach((loader) => {
                loader(bind);
            });
        },
        createChild,
    };
    container = api;
    return api;
}
exports.Container = Container;
//# sourceMappingURL=simple-container.js.map